import { useState, useRef } from "react";
import { invoke } from "../lib/bridge";
import { DecryptedAvatar } from "./DecryptedAvatar";
import { lastOnlineLabel } from "./FamilyTab";
import { Modal } from "./ui/Modal";
import { MemberSettings } from "./MemberSettings";

interface MemberInfo {
  isSelf: boolean;
  avatarUrl: string | null;
}

interface MemberViewProps {
  name: string;
  isMe: boolean;
  avatarUrl: string | null;
  online: boolean;
  lastActiveAt: string | null;
  onBack: () => void;
  onLinkDevice?: () => void;
  onNameChanged: (newName: string) => void;
  onAvatarChanged?: (avatarUrl: string | null) => void;
  onLeaveFamily: () => void;
}

export function MemberView({
  name,
  isMe,
  avatarUrl,
  online,
  lastActiveAt,
  onBack,
  onLinkDevice,
  onNameChanged,
  onAvatarChanged,
  onLeaveFamily,
}: MemberViewProps) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const [saving, setSaving] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === name) {
      setEditing(false);
      setEditName(name);
      return;
    }
    setSaving(true);
    try {
      await invoke("update_member_name", { newName: trimmed });
      onNameChanged(trimmed);
      setEditing(false);
    } catch (e) {
      console.error("Failed to update name:", e);
    }
    setSaving(false);
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      await invoke("leave_family");
      onLeaveFamily();
    } catch (e) {
      console.error("Failed to leave family:", e);
      setLeaving(false);
      setShowLeaveConfirm(false);
    }
  };

  const handleAvatarTap = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Pass the raw File; the bridge resizes (800px) and encrypts it with the
      // group key before it reaches the server. Decrypted display is task 61.
      await invoke("upload_avatar", { file });
      // upload_avatar returns only ok/failure, not the new blob token (avatars
      // are read back via list_members, not returned synchronously). Re-fetch
      // our own member row so the screen shows the new avatar immediately
      // instead of the stale one baked into navigation state when this screen
      // opened.
      if (onAvatarChanged) {
        const members = await invoke<MemberInfo[]>("list_members");
        const self = Array.isArray(members) ? members.find((m) => m.isSelf) : undefined;
        onAvatarChanged(self?.avatarUrl ?? null);
      }
    } catch (err) {
      console.error("Failed to upload avatar:", err);
    }
    setUploading(false);
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="container" style={{ padding: "16px" }}>
      <div className="header-row">
        <h1 className="header-title">Member</h1>
        <button onClick={onBack} className="btn-close" aria-label="Close">&times;</button>
      </div>

      <div className="member-detail">
        <div className="avatar-section">
          <div style={{ position: "relative" }}>
            <DecryptedAvatar token={avatarUrl} name={name} size="large" onClick={isMe ? handleAvatarTap : undefined} />
            {uploading && <div className="avatar-uploading">...</div>}
          </div>
          {isMe && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelected}
                style={{ display: "none" }}
              />
              <button onClick={handleAvatarTap} disabled={uploading} className="btn-secondary btn-small" style={{ marginTop: "8px" }}>
                {uploading ? "Uploading..." : "Change Photo"}
              </button>
            </>
          )}
        </div>

        <div className="member-detail-row">
          <span className="field-label">Name</span>
          {editing ? (
            <form
              onSubmit={(e) => { e.preventDefault(); handleSave(); }}
              className="member-edit-form"
            >
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="field-input"

              />
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <button type="submit" disabled={saving} className="btn-primary">
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setEditName(name); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="member-detail-value">
              <span>{name}</span>
              {isMe && (
                <button onClick={() => setEditing(true)} className="btn-secondary btn-small">
                  Edit
                </button>
              )}
            </div>
          )}
          {isMe && !editing && (
            <p className="member-detail-hint" style={{ margin: "4px 0 0" }}>This is you.</p>
          )}
        </div>

        <div className="member-detail-row">
          <span className="field-label">Status</span>
          <div className="member-detail-value">
            <span className={`online-dot ${online ? "online" : "offline"}`} />
            <span>{online ? "Online" : "Offline"}</span>
          </div>
        </div>

        <div className="member-detail-row">
          <span className="field-label">Last Active</span>
          <span>{online ? "Now" : lastOnlineLabel(lastActiveAt)}</span>
        </div>

        {isMe && <MemberSettings />}

        {isMe && (
          <div style={{ marginTop: "32px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {onLinkDevice && (
              <button onClick={onLinkDevice} className="btn-secondary btn-full">
                Link a device
              </button>
            )}
            <button
              onClick={() => setShowLeaveConfirm(true)}
              className="btn-danger btn-full"
            >
              Leave this Family
            </button>
          </div>
        )}
      </div>

      <Modal
        open={showLeaveConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        title="Leave this Family?"
      >
        <h3>Leave this Family?</h3>
        <p>Your membership is permanently deleted on all your devices, and family data is removed from this device. Lists, notes, recipes, and other content you created stay available to your family members. This cannot be undone.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "16px" }}>
          <button
            onClick={handleLeave}
            disabled={leaving}
            className="btn-danger btn-full"
          >
            {leaving ? "Leaving..." : "Leave"}
          </button>
          <button
            onClick={() => setShowLeaveConfirm(false)}
            disabled={leaving}
            className="btn-secondary btn-full"
          >
            Cancel
          </button>
        </div>
      </Modal>

    </div>
  );
}
